import { DataTable } from "../../../../../../../../../components/DataTable";
import * as React from "react";
import { useMemo, useState } from "react";
import { CURRENCY_SYMBOLS, SORT_DIRECTIONS, translateSubElementString } from "../../../../../../../../../utils";
import { Flex, Text } from "@chakra-ui/react";
import { NumberField } from "../../../../../../../../../components/NumberField";
import { useTranslation } from "react-i18next";
import { hideAppSpinner } from "../../../../../../../../../context/Store/slices/appSpinnerSlice";
import { useGetEstimatedPurchasedGoodsAndServices } from "../../../../../../../../../hooks/query/reports";
import { useDispatch } from "react-redux";

function EstimatedPurchasedGoodsAndServicesTab({ data, reportId }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.scopeThree.inputs.purchasedGoodsAndServices.estimated"
  });
  const dispatch = useDispatch();

  const [purchaseItemsList, setPurchaseItemsList] = useState([]);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [`id,${SORT_DIRECTIONS.DESC}`],
    totalElements: 0,
    totalPages: 0
  });

  const { totalPages, queryPageIndex, queryPageSize, totalElements } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...newValObject
      };
    });
  };

  const columns = useMemo(
    () => {
      return [
        {
          header: t("gridColumns.supplierName"),
          accessorKey: "supplierName",
          size: 120
        },
        {
          header: t("gridColumns.location"),
          accessorKey: "location",
          size: 100
        },
        {
          header: t("gridColumns.productCode"),
          accessorKey: "productCodeAndCategory",
          size: 180
        },
        {
          header: t("gridColumns.itemDescription"),
          accessorKey: "itemDescription",
          size: 150
        },
        {
          header: t("gridColumns.amountSpent"),
          accessorKey: "spentMoneyAmountFormatted",
          size: 120
        },
        {
          header: translateSubElementString("reportsPage.scopeThree.inputs.purchasedGoodsAndServices.estimated.gridColumns.tCO2e"),
          accessorKey: "co2Emissions",
          size: 100
        }
      ];
    },
    [t]
  );

  const {
    isLoading,
    isFetching
  } = useGetEstimatedPurchasedGoodsAndServices(
    reportId,
    { page: queryPageIndex, size: queryPageSize, sort: "id,desc" },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const purchaseItems = pageContent.map((item) => {
          return {
            id: item.id,
            supplierName: item.supplierName,
            location: item.location,
            productCodeAndCategory: `${item.productCode} - ${item.productCategory}`,
            itemDescription: item.itemDescription,
            spentMoneyAmountFormatted: `${CURRENCY_SYMBOLS[item.currency] ?? ""}${Number(item.spentMoneyAmount)?.toLocaleString("en-GB", { maximumFractionDigits: 4 })}`,
            co2Emissions: item.co2Emissions
          };
        });

        setPurchaseItemsList(purchaseItems);
        if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when page size is changed to larger and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => console.log(err),
      onSettled: () => {
        dispatch(hideAppSpinner());
      },
      cacheTime: 0
    }
  );

  return (
    <>
      <DataTable
        data={purchaseItemsList}
        columns={columns}
        isLoading={isLoading || isFetching}
        containerHeight="300px"
        marginBlockEnd={4}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
      />
      <Flex>
        <Text size="sm" fontWeight="bold">
          {translateSubElementString(
            "reportsPage.scopeThree.inputs.purchasedGoodsAndServices.estimated.totalEmissions"
          )}
        </Text>
        <NumberField value={data?.estimatedPurchasedGoodsAndServices?.totalCo2Emissions ?? ""} marginStart="2" />
      </Flex>
    </>
  );
}

export default EstimatedPurchasedGoodsAndServicesTab;